import { Injectable, inject, ApplicationRef, DestroyRef } from '@angular/core';
import { Router, RoutesRecognized, NavigationStart } from '@angular/router';
import { filter, first } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PerformanceMetricsService } from './performance-metrics.service';
import { ScreenName } from './performance-metrics.interfaces';
import { ScreenUrlMap, ScreenConfigs } from './performance-metrics.config';

/**
 * Сервис для инициализации метрик производительности
 * Автоматически отслеживает старт приложения и навигацию по экранам
 */
@Injectable({
  providedIn: 'root',
})
export class PerformanceMetricsBootstrapService {
  private readonly performanceMetricsService: PerformanceMetricsService = inject(PerformanceMetricsService);
  private readonly router: Router = inject(Router);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private isInitialized: boolean = false;

  public init(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;

    // Отслеживаем старт приложения
    this.trackAppStart();

    // Отслеживаем навигацию по экранам
    this.setupNavigationTracking();
  }

  private trackAppStart(): void {
    void this.performanceMetricsService.trackStart();

    this.appRef.isStable
      .pipe(
        filter((isStable: boolean) => isStable),
        first(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.performanceMetricsService.markAppStable();
      });
  }

  private setupNavigationTracking(): void {
    // Используем NavigationStart для МАКСИМАЛЬНО ранней установки currentScreenName
    // Это событие срабатывает СРАЗУ при начале навигации, ДО RoutesRecognized и ДО загрузки компонентов
    // Это позволяет установить currentScreenName до того, как начнутся любые HTTP-запросы
    this.router.events
      .pipe(
        filter((event: unknown): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((navigationStartEvent: NavigationStart) => {
        const url: string = navigationStartEvent.url;
        const screenName: ScreenName | null = this.getScreenNameFromUrl(url);

        if (screenName) {
          // Устанавливаем currentScreenName СРАЗУ при начале навигации
          // Это позволяет сетевые запросы видеть текущий экран до создания спана экрана
          this.performanceMetricsService.startScreenTracking(screenName);
        }
      });

    // Также слушаем RoutesRecognized для случаев с редиректами
    // Если URL изменился из-за редиректа, обновим currentScreenName
    this.router.events
      .pipe(
        filter((event: unknown): event is RoutesRecognized => event instanceof RoutesRecognized),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((routesRecognizedEvent: RoutesRecognized) => {
        const url: string = routesRecognizedEvent.urlAfterRedirects || routesRecognizedEvent.url;
        const screenName: ScreenName | null = this.getScreenNameFromUrl(url);

        // Если URL изменился из-за редиректа, обновим currentScreenName
        if (screenName) {
          this.performanceMetricsService.startScreenTracking(screenName);
        }
      });
  }

  private getScreenNameFromUrl(url: string): ScreenName | null {
    // Нормализуем URL: убираем завершающий слеш для сравнения (кроме корневого пути)
    const normalizedUrl: string = url !== '/' && url.endsWith('/') ? url.slice(0, -1) : url;

    for (const [pattern, screenName] of ScreenUrlMap) {
      if (typeof pattern === 'string') {
        // Сравниваем как точное совпадение, так и с завершающим слешем
        const matches: boolean = normalizedUrl === pattern || url === pattern || url === `${pattern}/`;
        if (matches) {
          return screenName;
        }
      } else if (pattern instanceof RegExp) {
        if (pattern.test(url)) {
          return screenName;
        }
      }
    }

    // Fallback: если имя страницы не определено, берем первую часть роута
    // Например, для /catalog/detskie-molochnye-produkty-56/ берем 'catalog'
    // Проверяем, есть ли конфигурация для этого имени
    const firstRoutePart: string | null = this.getFirstRoutePart(url);
    if (firstRoutePart) {
      // Проверяем, есть ли конфигурация для этого имени в ScreenConfigs
      // Используем прямое сравнение с известными именами экранов
      const fallbackScreenName: ScreenName | string | null = this.getScreenNameFromRoutePart(firstRoutePart);
      if (fallbackScreenName && ScreenConfigs.has(fallbackScreenName)) {
        return fallbackScreenName as ScreenName;
      }
    }

    return null;
  }

  private getFirstRoutePart(url: string): string | null {
    // Убираем ведущий и завершающий слеши
    const cleanUrl: string = url.replace(/^\/+|\/+$/g, '');
    if (!cleanUrl) {
      return null;
    }

    // Берем первую часть роута (до первого слеша)
    const parts: string[] = cleanUrl.split('/');
    return parts[0] || null;
  }

  private getScreenNameFromRoutePart(routePart: string): ScreenName | string | null {
    // Маппинг первой части роута на имя экрана
    const routeToScreenMap: Record<string, ScreenName | string> = {
      catalog: ScreenName.CATALOG_LISTING,
      basket: ScreenName.CART,
      product: ScreenName.PRODUCT,
      ordering: ScreenName.CHECKOUT,
    };

    return routeToScreenMap[routePart] || null;
  }
}
